/**
 * Path mechanics sub-region inspect view per scope §6.3. Atlas Day 2.5 P6.
 *
 * Flat region at (substrate, calculation) cell. Reads
 * FIELD_CLASSIFICATIONS.fulfillment_path_config (6 fields per Phase 0 #9
 * substrate verification).
 *
 * L7 surfaces untraced architectural fact per Atlas Day 2 Phase 0 #10
 * confirmation (fpc.provenance column absent — Day 82 P6.1 6-trigger
 * scope didn't cover fpc).
 *
 * D8 lock applied at surfaceLine: "ISG operates 6 fulfillment approaches;
 * 1 dropship path inactive" framing per IS-vs-DOING filter.
 */

import type { JSX } from 'react';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import { PATH_MECHANICS_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function PathMechanicsInspect(): JSX.Element {
  const fpcFields = FIELD_CLASSIFICATIONS.fulfillment_path_config;
  const fieldEntries = Object.entries(fpcFields);

  const strategicFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'strategic');
  const mechanicalFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'mechanical');
  const operationalFields = fieldEntries.filter(([, c]) => c.layer6Authority === 'operational');
  const naLayer6Fields = fieldEntries.filter(([, c]) => c.layer6Authority === undefined);

  return (
    <InspectShell
      subRegionName="Path mechanics"
      surfaceLine="ISG operates 6 fulfillment approaches; 1 dropship path inactive. Engine branches by path type and metadata flags. → composed into Path eligibility above"
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.3: per-field split. <code>path_type</code> = <strong>mechanical</strong>{' '}
          (path mechanics determine type, not ISG choice — Day 86 A3 schema-shape).{' '}
          Metadata flags (<code>applies_inbound_shipping</code>, <code>applies_prep_fee</code>,{' '}
          <code>payout_param_key</code>) = <strong>mechanical</strong> (follow path mechanics).{' '}
          <code>is_active</code> = <strong>judgment</strong> (operating availability toggle).
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>Per scope §6.3 L5: mostly mechanical (metadata flags follow path mechanics).</p>
        <div className="mt-2">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Mechanical section ({mechanicalFields.length} fields) — schema-shape + path mechanics
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {mechanicalFields.map(([field, c]) => (
              <li key={field}>
                <code>{field}</code>
                {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
              </li>
            ))}
          </ul>
        </div>
        {strategicFields.length > 0 && (
          <div className="mt-3">
            <h3 className="text-xs font-semibold text-ink-bright mb-1">
              Strategic section ({strategicFields.length} fields)
            </h3>
            <ul className="list-disc pl-5 text-xs space-y-0.5">
              {strategicFields.map(([field, c]) => (
                <li key={field}>
                  <code>{field}</code>
                  {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>
          Per scope §6.3 L6: <code>is_active</code> = operational (toggling availability is
          operating-tuning). Metadata flags = layer 6 N/A (not authority-applicable; path
          mechanics determines metadata).
        </p>
        <div className="mt-2">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Operational-altitude ({operationalFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {operationalFields.map(([field]) => (
              <li key={field}>
                <code>{field}</code>
              </li>
            ))}
          </ul>
        </div>
        {naLayer6Fields.length > 0 && (
          <div className="mt-3">
            <h3 className="text-xs font-semibold text-ink-bright mb-1">
              Region-altitude ({naLayer6Fields.length} fields, layer 6 N/A per three-state
              applicability)
            </h3>
            <ul className="list-disc pl-5 text-xs space-y-0.5">
              {naLayer6Fields.map(([field]) => (
                <li key={field}>
                  <code>{field}</code>
                </li>
              ))}
            </ul>
          </div>
        )}
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Per scope §6.3 L7 + Atlas Day 2 Phase 0 #10: <code>fulfillment_path_config.provenance</code>{' '}
          column does NOT exist. fpc was not in the Day 82 P6.1 6-trigger cohort. Path
          mechanics renders <strong>untraced pattern</strong> as architectural fact per scope
          §6 layer 7 three-pattern framing (fully-traced / backfill-traced / untraced).
        </p>
        <p>
          Behavior-as-preservation framing (scope §4.3 sub-corollary): untraced state
          preserves engine performance — fpc edits are infrequent operator-altitude changes
          (path toggles + metadata adjustments) where lacking per-row trace doesn&apos;t
          impair audit-altitude needs. Trace surfaces via git history of migration files
          when needed.
        </p>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>
          Curated map (verified as of 2026-05-27 per scope §8 staleness marker; Day 60b
          consumer rollup re-verified Phase 0 #6 — 23 consumer files unchanged from Atlas
          Day 2):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {PATH_MECHANICS_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Per scope §6.3 L9: split per altitude. Clean-reversible at toggle altitude (is_active
          reverts cleanly with 60s cache invalidation). Structurally-irreversible at metadata
          flag altitude (path_type + applies_inbound_shipping + applies_prep_fee +
          payout_param_key are schema-shape; code change to engine RPCs required to alter
          past calculations). Per-field:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {fieldEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer9Reversibility}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution per scope §6.3 L11. Path mechanics contributes to
          ISG&apos;s position by encoding which fulfillment approaches ISG operates;
          surfaces in title block sentence 2 as channel/approach enumeration (Atlas Day 1
          substrate-driven render: 4 marketplaces alphabetical per D4 lock; path-altitude
          detail surfaces here in Path eligibility rule region inspect — Atlas Day 3 §6.6.3).
        </p>
      </LayerSection>
    </InspectShell>
  );
}
